import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useSessionEhrProvider } from '@/hooks/integrations/useSessionEhrProvider';
import { useQueryAvailableStandardizedSections } from '@/hooks/query/integrations/general/useQueryAvailableStandardizedSections';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { useMemo, type ReactNode } from 'react';
import { SectionEhrContext, type SectionLinkHandler } from './section-ehr-context';

export type { SectionLinkHandler } from './section-ehr-context';

export function SectionEhrProvider({
  children,
  onSectionLink,
}: {
  children: ReactNode;
  onSectionLink?: SectionLinkHandler;
}) {
  // Bind to the ACTIVE session's provider (not the first completed onboarding
  // session) so the editor and its field catalogue agree for multi-integration
  // users. Off the session page (template surfaces) useCurrentSessionId() is ''
  // and this resolves undefined, falling back to the default behaviour.
  const sessionId = useCurrentSessionId();
  const { sessionEhrProvider } = useSessionEhrProvider(sessionId);
  const { integrationEhrProvider, canSyncSectionalNotes } =
    useIntegrationEhrInfo(sessionEhrProvider);
  const { data: availableStandardizedSections } =
    useQueryAvailableStandardizedSections(sessionEhrProvider);

  const value = useMemo(
    () => ({
      integrationEhrProvider,
      canSyncSectionalNotes,
      availableStandardizedSections,
      onSectionLink,
    }),
    [
      integrationEhrProvider,
      canSyncSectionalNotes,
      availableStandardizedSections,
      onSectionLink,
    ]
  );

  return (
    <SectionEhrContext.Provider value={value}>{children}</SectionEhrContext.Provider>
  );
}
